// src/admin/AdminTranscriptManager.js - Lọc Đánh Giá Cũ (Thêm tùy chọn Thiếu 1 cột điểm)
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { firestore } from '../firebaseConfig';
import { collection, doc, getDocs, writeBatch, query, orderBy } from 'firebase/firestore';
import { StudentContext } from '../StudentContext';
import * as XLSX from 'xlsx';
import './AdminTranscriptManager.css';

// Trọng số tính điểm
const WEIGHTS = { oral: 1, fifteenMinutes: 1, onePeriod: 2, exam: 4 };
const REQUIRED_SEMESTER_FIELDS = ['oral', 'fifteenMinutes', 'onePeriod', 'exam'];

const AdminTranscriptManager = () => {
  const { currentYearId: contextYearId } = useContext(StudentContext);
  const outletContext = useOutletContext();
  const sidebarSelectedClass = outletContext?.selectedClassForOutlet || 'all';

  const [years, setYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [classesList, setClassesList] = useState([]);
  const [studentsList, setStudentsList] = useState([]);

  // Dữ liệu bảng điểm: original (gốc Firestore) & working (đang nhập sửa)
  const [originalTranscripts, setOriginalTranscripts] = useState({});
  const [workingTranscripts, setWorkingTranscripts] = useState({});

  // Bộ lọc
  const [filterClassId, setFilterClassId] = useState('ALL');
  const [filterEvaluation, setFilterEvaluation] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [pinModifiedToTop, setPinModifiedToTop] = useState(false);

  // Sắp xếp cột: key & direction ('asc' | 'desc')
  const [sortConfig, setSortConfig] = useState({ key: 'className', direction: 'asc' });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 1. Tự động đồng bộ khi chọn Lớp từ Sidebar của AdminManagement
  useEffect(() => {
    if (sidebarSelectedClass && sidebarSelectedClass !== 'all') {
      setFilterClassId(sidebarSelectedClass);
    } else if (sidebarSelectedClass === 'all') {
      setFilterClassId('ALL');
    }
  }, [sidebarSelectedClass]);

  // 2. Tải danh sách Niên Khóa
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const q = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
        const snap = await getDocs(q);
        const yList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setYears(yList);

        if (yList.length > 0) {
          const defaultYear = yList.find(y => y.isCurrent)?.id || contextYearId || yList[0].id;
          setSelectedYearId(defaultYear);
        }
      } catch (err) {
        console.error('Lỗi khi tải niên khóa:', err);
      }
    };
    fetchYears();
  }, [contextYearId]);

  // 3. Tải toàn bộ Danh mục Lớp, Thiếu Nhi & Bảng Điểm theo Niên Khóa đã chọn
  useEffect(() => {
    const loadYearData = async () => {
      if (!selectedYearId) return;
      setLoading(true);
      try {
        const [classesSnap, studentsSnap, enrollmentsSnap] = await Promise.all([
          getDocs(collection(firestore, `academic_years/${selectedYearId}/classes`)),
          getDocs(collection(firestore, 'students')),
          getDocs(collection(firestore, `academic_years/${selectedYearId}/enrollments`))
        ]);

        const classesMap = {};
        const cList = classesSnap.docs.map(d => {
          const data = d.data();
          classesMap[d.id] = data.name || d.id;
          return { id: d.id, ...data };
        });
        setClassesList(cList);

        const studentsProfileMap = {};
        studentsSnap.docs.forEach(d => {
          studentsProfileMap[d.id] = d.data();
        });

        const origTrMap = {};
        const loadedStudents = [];

        enrollmentsSnap.docs.forEach(d => {
          const enr = d.data();
          const sId = enr.studentId || d.id;
          const profile = studentsProfileMap[sId] || {};

          const tr = enr.transcript || {};
          origTrMap[sId] = {
            semester1: {
              oral: String(tr.semester1?.oral ?? ''),
              fifteenMinutes: String(tr.semester1?.fifteenMinutes ?? ''),
              onePeriod: String(tr.semester1?.onePeriod ?? ''),
              exam: String(tr.semester1?.exam ?? ''),
              average: String(tr.semester1?.average ?? ''),
              diligence: String(tr.semester1?.diligence ?? '')
            },
            semester2: {
              oral: String(tr.semester2?.oral ?? ''),
              fifteenMinutes: String(tr.semester2?.fifteenMinutes ?? ''),
              onePeriod: String(tr.semester2?.onePeriod ?? ''),
              exam: String(tr.semester2?.exam ?? ''),
              average: String(tr.semester2?.average ?? ''),
              diligence: String(tr.semester2?.diligence ?? '')
            },
            yearly: {
              average: String(tr.yearly?.average ?? ''),
              diligence: String(tr.yearly?.diligence ?? ''),
              rank: String(tr.yearly?.rank ?? ''),
              oralExam: String(tr.yearly?.oralExam ?? '')
            }
          };

          loadedStudents.push({
            id: sId,
            holyName: profile.holyName || '',
            fullName: profile.fullName || sId,
            status: profile.status || 'Còn đi học',
            classId: enr.classId || 'CHUA_XEP_LOP',
            className: classesMap[enr.classId] || enr.classId || 'Chưa xếp lớp'
          });
        });

        setStudentsList(loadedStudents);
        setOriginalTranscripts(origTrMap);
        setWorkingTranscripts(JSON.parse(JSON.stringify(origTrMap)));
      } catch (err) {
        console.error('Lỗi khi tải bảng điểm toàn trường:', err);
      } finally {
        setLoading(false);
      }
    };

    loadYearData();
  }, [selectedYearId]);

  // --- CÁC HÀM TÍNH TOÁN ĐIỂM SỐ & TRẠNG THÁI TỰ ĐỘNG ---
  const calculateSemesterAvg = (semScores = {}) => {
    const allPresent = REQUIRED_SEMESTER_FIELDS.every(
      f => semScores[f] !== '' && !isNaN(parseFloat(semScores[f]))
    );
    if (!allPresent) return '';

    let total = 0;
    let totalWeight = 0;
    REQUIRED_SEMESTER_FIELDS.forEach(f => {
      const val = parseFloat(semScores[f]);
      const w = WEIGHTS[f];
      total += val * w;
      totalWeight += w;
    });
    return (total / totalWeight).toFixed(2);
  };

  const calculateYearlyAvg = (sem1AvgStr, sem2AvgStr) => {
    const s1 = parseFloat(sem1AvgStr);
    const s2 = parseFloat(sem2AvgStr);
    if (!isNaN(s1) && !isNaN(s2)) {
      return ((s1 + s2) / 2).toFixed(2);
    }
    return '';
  };

  const evaluateStudentStatus = (tr = {}) => {
    const sem1 = tr.semester1 || {};
    const sem2 = tr.semester2 || {};
    const yearly = tr.yearly || {};

    let missingCount = 0;
    REQUIRED_SEMESTER_FIELDS.forEach(f => {
      if (!sem1[f] || isNaN(parseFloat(sem1[f]))) missingCount++;
      if (!sem2[f] || isNaN(parseFloat(sem2[f]))) missingCount++;
    });

    if (missingCount > 0) {
      return {
        label: `Thiếu ${missingCount} cột điểm`,
        type: missingCount === 1 ? 'missing-1' : 'missing',
        missingCount
      };
    }

    const yearlyAvg = parseFloat(yearly.average);
    const hasBadDiligence =
      String(sem1.diligence).toLowerCase() === 'kém' ||
      String(sem2.diligence).toLowerCase() === 'kém' ||
      String(yearly.diligence).toLowerCase() === 'kém';

    if (hasBadDiligence) {
      return { label: 'Ở lại lớp (Chuyên cần kém)', type: 'retained-diligence', missingCount: 0 };
    }

    if (!isNaN(yearlyAvg) && yearlyAvg < 5.0) {
      return { label: 'Ở lại lớp (Điểm kém)', type: 'retained-score', missingCount: 0 };
    }

    return { label: 'Được lên lớp', type: 'promoted', missingCount: 0 };
  };

  // Thay đổi điểm số
  const handleScoreChange = (studentId, semester, field, value) => {
    let corrected = value.replace(/,/g, '.');
    const pattern = /^[0-9]*\.?[0-9]*$/;
    if (!pattern.test(corrected) && corrected !== '') return;

    setWorkingTranscripts(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated[studentId]) updated[studentId] = { semester1: {}, semester2: {}, yearly: {} };
      if (!updated[studentId][semester]) updated[studentId][semester] = {};

      updated[studentId][semester][field] = corrected;

      if (semester !== 'yearly' || field !== 'oralExam') {
        const sem1Avg = calculateSemesterAvg(updated[studentId].semester1);
        const sem2Avg = calculateSemesterAvg(updated[studentId].semester2);
        updated[studentId].semester1.average = sem1Avg;
        updated[studentId].semester2.average = sem2Avg;
        updated[studentId].yearly.average = calculateYearlyAvg(sem1Avg, sem2Avg);
      }

      const statusObj = evaluateStudentStatus(updated[studentId]);
      updated[studentId].yearly.rank = statusObj.label;

      return updated;
    });
  };

  // Thay đổi chuyên cần
  const handleDiligenceChange = (studentId, semester, value) => {
    setWorkingTranscripts(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated[studentId]) updated[studentId] = { semester1: {}, semester2: {}, yearly: {} };
      if (!updated[studentId][semester]) updated[studentId][semester] = {};

      updated[studentId][semester].diligence = value;

      const statusObj = evaluateStudentStatus(updated[studentId]);
      updated[studentId].yearly.rank = statusObj.label;

      return updated;
    });
  };

  const isCellModified = (studentId, semester, field) => {
    const origVal = originalTranscripts[studentId]?.[semester]?.[field] ?? '';
    const currVal = workingTranscripts[studentId]?.[semester]?.[field] ?? '';
    return String(origVal).trim() !== String(currVal).trim();
  };

  // Thống kê thay đổi
  const modifiedSummary = useMemo(() => {
    const modifiedList = [];
    let totalCells = 0;

    studentsList.forEach(st => {
      const sId = st.id;
      const orig = originalTranscripts[sId] || {};
      const curr = workingTranscripts[sId] || {};

      let changedFields = [];
      ['semester1', 'semester2', 'yearly'].forEach(sem => {
        const fields = sem === 'yearly' ? ['diligence', 'oralExam'] : ['oral', 'fifteenMinutes', 'onePeriod', 'exam', 'diligence'];
        fields.forEach(f => {
          const valO = String(orig[sem]?.[f] ?? '').trim();
          const valC = String(curr[sem]?.[f] ?? '').trim();
          if (valO !== valC) {
            changedFields.push(`${sem}.${f}: "${valO}" ➔ "${valC}"`);
            totalCells++;
          }
        });
      });

      if (changedFields.length > 0) {
        modifiedList.push({
          student: st,
          changedFields
        });
      }
    });

    return {
      modifiedList,
      totalStudents: modifiedList.length,
      totalCells
    };
  }, [studentsList, originalTranscripts, workingTranscripts]);

  // Xử lý sắp xếp tiêu đề cột
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Lọc và Sắp Xếp Danh Sách Hiển Thị
  const displayStudents = useMemo(() => {
    const modifiedIdSet = new Set(modifiedSummary.modifiedList.map(m => m.student.id));

    let list = studentsList.filter(st => {
      const matchClass = filterClassId === 'ALL' || st.classId === filterClassId;
      const search = searchTerm.toLowerCase();
      const matchSearch =
        st.fullName.toLowerCase().includes(search) ||
        st.holyName.toLowerCase().includes(search) ||
        st.id.toLowerCase().includes(search) ||
        st.className.toLowerCase().includes(search);

      // 🌟 Lọc cố định theo ĐÁNH GIÁ CŨ (originalTranscripts)
      const origTr = originalTranscripts[st.id] || {};
      const oldStatusObj = evaluateStudentStatus(origTr);

      let matchEval = true;
      if (filterEvaluation !== 'ALL') {
        if (filterEvaluation === 'missing') {
          matchEval = oldStatusObj.missingCount > 0;
        } else if (filterEvaluation === 'missing-1') {
          matchEval = oldStatusObj.missingCount === 1;
        } else {
          matchEval = oldStatusObj.type === filterEvaluation;
        }
      }

      return matchClass && matchSearch && matchEval;
    });

    list.sort((a, b) => {
      if (pinModifiedToTop) {
        const aMod = modifiedIdSet.has(a.id) ? 1 : 0;
        const bMod = modifiedIdSet.has(b.id) ? 1 : 0;
        if (aMod !== bMod) return bMod - aMod;
      }

      const trA = workingTranscripts[a.id] || {};
      const trB = workingTranscripts[b.id] || {};
      const origTrA = originalTranscripts[a.id] || {};
      const origTrB = originalTranscripts[b.id] || {};

      let valA, valB;

      switch (sortConfig.key) {
        case 'id':
          valA = a.id;
          valB = b.id;
          break;
        case 'holyName':
          valA = a.holyName;
          valB = b.holyName;
          break;
        case 'fullName':
          valA = a.fullName.split(' ').pop();
          valB = b.fullName.split(' ').pop();
          break;
        case 'className':
          valA = a.className;
          valB = b.className;
          break;
        case 'status':
          valA = a.status;
          valB = b.status;
          break;
        case 'sem1Avg':
          valA = parseFloat(trA.semester1?.average) || 0;
          valB = parseFloat(trB.semester1?.average) || 0;
          break;
        case 'sem2Avg':
          valA = parseFloat(trA.semester2?.average) || 0;
          valB = parseFloat(trB.semester2?.average) || 0;
          break;
        case 'yearlyAvg':
          valA = parseFloat(trA.yearly?.average) || 0;
          valB = parseFloat(trB.yearly?.average) || 0;
          break;
        case 'rank':
          valA = trA.yearly?.rank || '';
          valB = trB.yearly?.rank || '';
          break;
        case 'oldEvaluation':
          valA = evaluateStudentStatus(origTrA).label;
          valB = evaluateStudentStatus(origTrB).label;
          break;
        case 'newEvaluation':
          valA = evaluateStudentStatus(trA).label;
          valB = evaluateStudentStatus(trB).label;
          break;
        default:
          valA = a.className;
          valB = b.className;
      }

      if (typeof valA === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB, 'vi')
          : valB.localeCompare(valA, 'vi');
      }

      return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
    });

    return list;
  }, [studentsList, filterClassId, filterEvaluation, searchTerm, pinModifiedToTop, modifiedSummary, workingTranscripts, originalTranscripts, sortConfig]);

  const handleResetAll = () => {
    if (!window.confirm('Hủy bỏ toàn bộ các điểm số vừa chỉnh sửa và quay lại bản gốc?')) return;
    setWorkingTranscripts(JSON.parse(JSON.stringify(originalTranscripts)));
  };

  const handleCommitSave = async () => {
    if (modifiedSummary.totalStudents === 0) return;

    setSaving(true);
    try {
      const CHUNK_SIZE = 400;
      const modifiedStudents = modifiedSummary.modifiedList;

      for (let i = 0; i < modifiedStudents.length; i += CHUNK_SIZE) {
        const chunk = modifiedStudents.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(firestore);

        chunk.forEach(({ student }) => {
          const docRef = doc(firestore, `academic_years/${selectedYearId}/enrollments`, student.id);
          batch.set(
            docRef,
            {
              transcript: workingTranscripts[student.id] || {}
            },
            { merge: true }
          );
        });

        await batch.commit();
      }

      setOriginalTranscripts(JSON.parse(JSON.stringify(workingTranscripts)));
      setShowConfirmModal(false);
      alert(`🎉 Đã lưu thành công bảng điểm cho ${modifiedSummary.totalStudents} thiếu nhi!`);
    } catch (err) {
      console.error('Lỗi khi lưu bảng điểm lô:', err);
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = () => {
    const data = [
      [
        'STT', 'Mã TN', 'Tên Thánh', 'Họ và Tên', 'Lớp',
        'HK1-Miệng', 'HK1-15p', 'HK1-1 Tiết', 'HK1-Thi', 'HK1-TB', 'HK1-Chuyên Cần',
        'HK2-Miệng', 'HK2-15p', 'HK2-1 Tiết', 'HK2-Thi', 'HK2-TB', 'HK2-Chuyên Cần',
        'Cả Năm-TBCN', 'Cả Năm-Chuyên Cần', 'Thi Vấn Đáp', 'Xếp Hạng',
        'Đánh Giá Cũ', 'Đánh Giá Mới', 'Tình Trạng'
      ]
    ];

    displayStudents.forEach((st, idx) => {
      const origTr = originalTranscripts[st.id] || {};
      const tr = workingTranscripts[st.id] || {};
      const oldStatus = evaluateStudentStatus(origTr);
      const newStatus = evaluateStudentStatus(tr);

      const isModified = ['semester1', 'semester2', 'yearly'].some(sem =>
        ['oral', 'fifteenMinutes', 'onePeriod', 'exam', 'diligence', 'oralExam'].some(f =>
          isCellModified(st.id, sem, f)
        )
      );

      data.push([
        idx + 1, st.id, st.holyName, st.fullName, st.className,
        tr.semester1?.oral || '', tr.semester1?.fifteenMinutes || '', tr.semester1?.onePeriod || '', tr.semester1?.exam || '', tr.semester1?.average || '', tr.semester1?.diligence || '',
        tr.semester2?.oral || '', tr.semester2?.fifteenMinutes || '', tr.semester2?.onePeriod || '', tr.semester2?.exam || '', tr.semester2?.average || '', tr.semester2?.diligence || '',
        tr.yearly?.average || '', tr.yearly?.diligence || '', tr.yearly?.oralExam || '', tr.yearly?.rank || '',
        oldStatus.label,
        isModified ? newStatus.label : '-',
        st.status
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'BangDiemToanTruong');
    XLSX.writeFile(wb, `BangDiem_ToanTruong_${selectedYearId}.xlsx`);
  };

  return (
    <div className="atm-container">
      {/* HEADER */}
      <div className="atm-header">
        <div>
          <h2 className="atm-title">📊 Quản Lý Bảng Điểm Toàn Trường</h2>
          <p className="atm-subtitle">
            Dành riêng cho Admin: Xem, chỉnh sửa, lọc theo Lớp/Sidebar, tự động tính điểm và theo dõi đánh giá cũ/mới cho toàn bộ học sinh.
          </p>
        </div>
        <button onClick={handleExportExcel} className="atm-btn atm-btn-excel">
          📥 Xuất Excel ({displayStudents.length})
        </button>
      </div>

      {/* THANH CÔNG CỤ: CHỌN NIÊN KHÓA, LỌC LỚP, BỘ LỌC ĐÁNH GIÁ CŨ */}
      <div className="atm-toolbar-card">
        <div className="atm-filter-group">
          <label className="atm-label">📅 Niên Khóa:</label>
          <select
            value={selectedYearId}
            onChange={e => setSelectedYearId(e.target.value)}
            className="atm-select"
            disabled={modifiedSummary.totalStudents > 0}
          >
            {years.map(y => (
              <option key={y.id} value={y.id}>
                {y.name} {y.isCurrent ? '⭐ [Hiện tại]' : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="atm-filter-group">
          <label className="atm-label">🏫 Lớp:</label>
          <select
            value={filterClassId}
            onChange={e => setFilterClassId(e.target.value)}
            className="atm-select"
          >
            <option value="ALL">-- Tất cả các lớp ({classesList.length} lớp) --</option>
            {classesList.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <label className="atm-label">📋 Đánh Giá Cũ:</label>
          <select
            value={filterEvaluation}
            onChange={e => setFilterEvaluation(e.target.value)}
            className="atm-select"
          >
            <option value="ALL">-- Tất cả đánh giá cũ --</option>
            <option value="promoted">🟢 Được lên lớp</option>
            <option value="missing">🟠 Thiếu cột điểm (Tất cả)</option>
            <option value="missing-1">🟠 Thiếu 1 cột điểm</option>
            <option value="retained-score">🔴 Ở lại lớp (Điểm kém)</option>
            <option value="retained-diligence">🔴 Ở lại lớp (Chuyên cần kém)</option>
          </select>

          <input
            type="text"
            placeholder="Tìm tên thiếu nhi, tên thánh, mã TN..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="atm-search-input"
          />
        </div>
      </div>

      {/* THANH THỐNG KÊ THAY ĐỔI & NÚT LƯU */}
      {modifiedSummary.totalStudents > 0 && (
        <div className="atm-pending-bar">
          <div className="atm-pending-info">
            ⚠️ <strong>ĐANG CÓ THAY ĐỔI CHƯA LƯU:</strong> Đã chỉnh sửa <strong>{modifiedSummary.totalStudents}</strong> thiếu nhi ({modifiedSummary.totalCells} ô điểm).
          </div>
          <div className="atm-pending-actions">
            <label className="atm-checkbox-label">
              <input
                type="checkbox"
                checked={pinModifiedToTop}
                onChange={e => setPinModifiedToTop(e.target.checked)}
              />
              📌 Đẩy em đã sửa lên đầu danh sách
            </label>
            <button onClick={handleResetAll} className="atm-btn atm-btn-cancel">
              ↩️ Hủy Thay Đổi
            </button>
            <button onClick={() => setShowConfirmModal(true)} className="atm-btn atm-btn-save">
              💾 LƯU THAY ĐỔI ({modifiedSummary.totalStudents})
            </button>
          </div>
        </div>
      )}

      {/* BẢNG ĐIỂM TOÀN TRƯỜNG */}
      <div className="atm-table-wrapper">
        <table className="atm-table">
          <thead>
            {/* HÀNG TIÊU ĐỀ 1 */}
            <tr className="atm-th-row-1">
              <th rowSpan="2" className="atm-th-fixed-1 atm-th-sortable" onClick={() => handleSort('className')}>
                STT
              </th>
              <th rowSpan="2" className="atm-th-fixed-1 atm-th-sortable" onClick={() => handleSort('id')}>
                Mã TN <span className="atm-sort-icon">{getSortIcon('id')}</span>
              </th>
              <th rowSpan="2" className="atm-th-fixed-1 atm-th-sortable" onClick={() => handleSort('holyName')}>
                Tên Thánh <span className="atm-sort-icon">{getSortIcon('holyName')}</span>
              </th>
              <th rowSpan="2" className="atm-sticky-head-name atm-th-sortable" onClick={() => handleSort('fullName')}>
                Họ và Tên <span className="atm-sort-icon">{getSortIcon('fullName')}</span>
              </th>
              <th rowSpan="2" className="atm-th-fixed-1 atm-th-sortable" onClick={() => handleSort('className')}>
                Lớp <span className="atm-sort-icon">{getSortIcon('className')}</span>
              </th>

              <th colSpan="6" className="atm-th-group-hk1">Học Kỳ I</th>
              <th colSpan="6" className="atm-th-group-hk2">Học Kỳ II</th>
              <th colSpan="4" className="atm-th-group-year">Cả Năm</th>

              {/* CỘT ĐÁNH GIÁ CŨ */}
              <th rowSpan="2" className="atm-th-fixed-1 atm-th-sortable" onClick={() => handleSort('oldEvaluation')}>
                Đánh Giá Cũ <span className="atm-sort-icon">{getSortIcon('oldEvaluation')}</span>
              </th>

              {/* CỘT ĐÁNH GIÁ MỚI */}
              <th rowSpan="2" className="atm-th-fixed-1 atm-th-sortable" onClick={() => handleSort('newEvaluation')}>
                Đánh Giá Mới <span className="atm-sort-icon">{getSortIcon('newEvaluation')}</span>
              </th>

              {/* CỘT TÌNH TRẠNG CHUYỂN VỀ CUỐI */}
              <th rowSpan="2" className="atm-th-fixed-1 atm-th-sortable" onClick={() => handleSort('status')}>
                Tình Trạng <span className="atm-sort-icon">{getSortIcon('status')}</span>
              </th>
            </tr>

            {/* HÀNG TIÊU ĐỀ 2 (CÁC CỘT CON) */}
            <tr className="atm-th-row-2">
              {/* HK1 */}
              <th>Miệng</th>
              <th>15p</th>
              <th>1 Tiết</th>
              <th>Thi</th>
              <th className="atm-th-sortable" onClick={() => handleSort('sem1Avg')}>
                TB {getSortIcon('sem1Avg')}
              </th>
              <th>C.Cần</th>

              {/* HK2 */}
              <th>Miệng</th>
              <th>15p</th>
              <th>1 Tiết</th>
              <th>Thi</th>
              <th className="atm-th-sortable" onClick={() => handleSort('sem2Avg')}>
                TB {getSortIcon('sem2Avg')}
              </th>
              <th>C.Cần</th>

              {/* CẢ NĂM */}
              <th className="atm-th-sortable" onClick={() => handleSort('yearlyAvg')}>
                TBCN {getSortIcon('yearlyAvg')}
              </th>
              <th>C.Cần</th>
              <th>Vấn Đáp</th>
              <th className="atm-th-sortable" onClick={() => handleSort('rank')}>
                Xếp Hạng {getSortIcon('rank')}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="23" style={{ padding: '40px', color: '#64748b' }}>
                  ⏳ Đang nạp bảng điểm toàn trường...
                </td>
              </tr>
            ) : displayStudents.length === 0 ? (
              <tr>
                <td colSpan="23" style={{ padding: '40px', color: '#64748b' }}>
                  Không tìm thấy thiếu nhi nào phù hợp với bộ lọc.
                </td>
              </tr>
            ) : (
              displayStudents.map((student, idx) => {
                const sId = student.id;
                const origTr = originalTranscripts[sId] || { semester1: {}, semester2: {}, yearly: {} };
                const tr = workingTranscripts[sId] || { semester1: {}, semester2: {}, yearly: {} };

                const sem1 = tr.semester1 || {};
                const sem2 = tr.semester2 || {};
                const yearly = tr.yearly || {};

                const oldStatus = evaluateStudentStatus(origTr);
                const newStatus = evaluateStudentStatus(tr);

                const isRowModified = ['semester1', 'semester2', 'yearly'].some(sem =>
                  ['oral', 'fifteenMinutes', 'onePeriod', 'exam', 'diligence', 'oralExam'].some(f =>
                    isCellModified(sId, sem, f)
                  )
                );

                return (
                  <tr key={sId} className={isRowModified ? 'atm-row-modified' : ''}>
                    <td>{idx + 1}</td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{sId}</td>
                    <td>{student.holyName || '-'}</td>
                    <td className="atm-col-name atm-sticky-col-name">{student.fullName}</td>
                    <td>
                      <span className="atm-class-badge">{student.className}</span>
                    </td>

                    {/* HK1 */}
                    <td>
                      <input
                        type="text"
                        value={sem1.oral ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester1', 'oral', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester1', 'oral') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={sem1.fifteenMinutes ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester1', 'fifteenMinutes', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester1', 'fifteenMinutes') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={sem1.onePeriod ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester1', 'onePeriod', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester1', 'onePeriod') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={sem1.exam ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester1', 'exam', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester1', 'exam') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td className="atm-avg-cell">{sem1.average || '-'}</td>
                    <td>
                      <select
                        value={sem1.diligence ?? ''}
                        onChange={e => handleDiligenceChange(sId, 'semester1', e.target.value)}
                        className={`atm-diligence-select ${isCellModified(sId, 'semester1', 'diligence') ? 'atm-cell-edited' : ''}`}
                      >
                        <option value="">-</option>
                        <option value="Tốt">Tốt</option>
                        <option value="Khá">Khá</option>
                        <option value="Trung bình">TB</option>
                        <option value="Kém">Kém</option>
                      </select>
                    </td>

                    {/* HK2 */}
                    <td>
                      <input
                        type="text"
                        value={sem2.oral ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester2', 'oral', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester2', 'oral') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={sem2.fifteenMinutes ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester2', 'fifteenMinutes', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester2', 'fifteenMinutes') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={sem2.onePeriod ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester2', 'onePeriod', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester2', 'onePeriod') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={sem2.exam ?? ''}
                        onChange={e => handleScoreChange(sId, 'semester2', 'exam', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'semester2', 'exam') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td className="atm-avg-cell">{sem2.average || '-'}</td>
                    <td>
                      <select
                        value={sem2.diligence ?? ''}
                        onChange={e => handleDiligenceChange(sId, 'semester2', e.target.value)}
                        className={`atm-diligence-select ${isCellModified(sId, 'semester2', 'diligence') ? 'atm-cell-edited' : ''}`}
                      >
                        <option value="">-</option>
                        <option value="Tốt">Tốt</option>
                        <option value="Khá">Khá</option>
                        <option value="Trung bình">TB</option>
                        <option value="Kém">Kém</option>
                      </select>
                    </td>

                    {/* CẢ NĂM */}
                    <td className="atm-avg-cell" style={{ fontWeight: 800 }}>{yearly.average || '-'}</td>
                    <td>
                      <select
                        value={yearly.diligence ?? ''}
                        onChange={e => handleDiligenceChange(sId, 'yearly', e.target.value)}
                        className={`atm-diligence-select ${isCellModified(sId, 'yearly', 'diligence') ? 'atm-cell-edited' : ''}`}
                      >
                        <option value="">-</option>
                        <option value="Tốt">Tốt</option>
                        <option value="Khá">Khá</option>
                        <option value="Trung bình">TB</option>
                        <option value="Kém">Kém</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={yearly.oralExam ?? ''}
                        onChange={e => handleScoreChange(sId, 'yearly', 'oralExam', e.target.value)}
                        className={`atm-score-input ${isCellModified(sId, 'yearly', 'oralExam') ? 'atm-cell-edited' : ''}`}
                        maxLength="4"
                      />
                    </td>
                    <td style={{ fontWeight: 700 }}>{yearly.rank || '-'}</td>

                    {/* ĐÁNH GIÁ CŨ */}
                    <td>
                      <span className={`atm-status-badge atm-status-${oldStatus.type}`}>
                        {oldStatus.label}
                      </span>
                    </td>

                    {/* ĐÁNH GIÁ MỚI */}
                    <td>
                      {isRowModified ? (
                        <span className={`atm-status-badge atm-status-${newStatus.type} atm-status-new-highlight`}>
                          ✨ {newStatus.label}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </td>

                    {/* TÌNH TRẠNG CHUYỂN VỀ CUỐI BẢNG */}
                    <td>
                      <span className={`atm-student-status-badge ${student.status === 'Còn đi học' ? 'active' : 'inactive'}`}>
                        {student.status || 'Còn đi học'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL THỐNG KÊ XÁC NHẬN LƯU VÀO FIRESTORE */}
      {showConfirmModal && (
        <div className="atm-modal-overlay">
          <div className="atm-modal-card">
            <h3 className="atm-modal-title">💾 Xác Nhận Lưu Bảng Điểm Lên Firestore</h3>
            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 10px 0' }}>
              Dưới đây là danh sách <strong>{modifiedSummary.totalStudents} thiếu nhi</strong> đã được chỉnh sửa:
            </p>

            <div className="atm-modal-body">
              <ul className="atm-modal-list">
                {modifiedSummary.modifiedList.map(({ student, changedFields }) => (
                  <li key={student.id} className="atm-modal-item">
                    <div>
                      <strong>{student.fullName}</strong> ({student.className})
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        Mã TN: {student.id} • Tên Thánh: {student.holyName || '-'}
                      </div>
                    </div>
                    <span style={{ fontSize: '12px', color: '#b45309', fontWeight: 700 }}>
                      {changedFields.length} ô sửa
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="atm-modal-actions">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={saving}
                className="atm-btn atm-btn-cancel"
              >
                Quay Lại Kiểm Tra
              </button>
              <button
                onClick={handleCommitSave}
                disabled={saving}
                className="atm-btn atm-btn-save"
              >
                {saving ? '⏳ Đang lưu Firestore...' : 'Xác Nhận Lưu Ngay'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTranscriptManager;